/**
 * OrganizasyonAgaciPanel — Sprint 4 F5a-2 (Admin UI)
 *
 * Backend: GET /api/organizasyon/chains/child/{personnel_id}
 *          GET /api/organizasyon/visibility/{viewer}/{target}
 *          GET /api/personnel  (mevcut)
 * Yetki: organizasyon.agac_gor
 *
 * UI:
 *   - Sol: Kök personelleri (parent_personnel_id NULL olanlar) liste
 *   - Sağ: Seçili kök için BFS chain — nested liste (depth ile indent)
 *   - Pasifleştirme butonları: her personelin yanında "Pasifleştir" → DeactivatePopupModal açar
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Network, RefreshCw, ChevronRight, ChevronDown, UserX,
  Users, Crown, Shield, HardHat, Briefcase, UserCheck
} from "lucide-react";
import { toast } from "sonner";

import apiClient from "@/api/client";
import { useAuthorization } from "@/auth/AuthorizationContext";
import DeactivatePopupModal from "./DeactivatePopupModal";


// Rol başına icon + renk
const ROLE_VISUAL = {
  admin: { icon: Shield, color: "text-red-600", bg: "bg-red-50" },
  baskan: { icon: Crown, color: "text-purple-600", bg: "bg-purple-50" },
  mudur: { icon: Crown, color: "text-red-600", bg: "bg-red-50" },
  mudur_yardimcisi: { icon: Briefcase, color: "text-orange-600", bg: "bg-orange-50" },
  bolge_muhendisi: { icon: UserCheck, color: "text-blue-600", bg: "bg-blue-50" },
  formen: { icon: HardHat, color: "text-yellow-600", bg: "bg-yellow-50" },
  park_gorevlisi: { icon: Users, color: "text-green-600", bg: "bg-green-50" },
  cim_ekibi: { icon: Users, color: "text-green-600", bg: "bg-green-50" },
};


export default function OrganizasyonAgaciPanel() {
  const { has } = useAuthorization();
  const canViewTree = has("organizasyon.agac_gor");
  const canDeactivate = has("personel.sil");

  const [allPersonnel, setAllPersonnel] = useState([]);
  const [selectedRootId, setSelectedRootId] = useState(null);
  const [chainData, setChainData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chainLoading, setChainLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // ---------- fetch ----------

  const fetchPersonnel = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/personnel");
      const list = Array.isArray(res.data) ? res.data : [];
      setAllPersonnel(list);
    } catch (err) {
      toast.error("Personel listesi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChain = useCallback(async (rootId) => {
    if (!rootId) {
      setChainData([]);
      return;
    }
    setChainLoading(true);
    try {
      const res = await apiClient.get(`/organizasyon/chains/child/${rootId}?include_self=true`);
      const chain = Array.isArray(res.data?.chain) ? res.data.chain : [];
      setChainData(chain);
      // Otomatik expand: ilk 2 level
      const autoExpand = new Set(
        chain.filter((n) => n.depth <= 1).map((n) => n.personnel_id)
      );
      setExpandedNodes(autoExpand);
    } catch (err) {
      toast.error("Hiyerarşi alınamadı: " + (err?.response?.data?.error?.message || err.message));
    } finally {
      setChainLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewTree) fetchPersonnel();
  }, [canViewTree, fetchPersonnel]);

  useEffect(() => {
    if (selectedRootId) fetchChain(selectedRootId);
  }, [selectedRootId, fetchChain]);

  // ---------- derived ----------

  // Kök personeller — parent_personnel_id null olanlar (admin/baskan/mudur + orphan)
  const roots = useMemo(
    () =>
      allPersonnel.filter(
        (p) => !p.parent_personnel_id && !p.deleted_at
      ),
    [allPersonnel]
  );

  // Chain'i parent → children mapping'e çevir (tree render için)
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const node of chainData) {
      const parent = node.parent_personnel_id || "__root__";
      if (!map.has(parent)) map.set(parent, []);
      map.get(parent).push(node);
    }
    return map;
  }, [chainData]);

  const rootNode = useMemo(
    () => chainData.find((n) => n.depth === 0),
    [chainData]
  );

  // ---------- handlers ----------

  const toggleNode = (id) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeactivateClick = (node) => {
    if (!canDeactivate) {
      toast.error("Pasifleştirme yetkiniz yok.");
      return;
    }
    setDeactivateTarget(node);
  };

  const handleDeactivateComplete = () => {
    setDeactivateTarget(null);
    fetchChain(selectedRootId);
    fetchPersonnel();
  };

  // ---------- render ----------

  if (!canViewTree) {
    return (
      <Card className="border-yellow-200">
        <CardContent className="py-6 text-center text-gray-500">
          Organizasyon ağacını görüntüleme yetkiniz yok (organizasyon.agac_gor).
        </CardContent>
      </Card>
    );
  }

  const renderNode = (node, depth) => {
    const children = childrenByParent.get(node.personnel_id) || [];
    const isExpanded = expandedNodes.has(node.personnel_id);
    const hasChildren = children.length > 0;
    const visual = ROLE_VISUAL[node.role_slug] || ROLE_VISUAL.park_gorevlisi;
    const Icon = visual.icon;

    return (
      <div key={node.personnel_id} style={{ marginLeft: `${depth * 20}px` }}>
        <div
          className={`flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 group ${visual.bg}`}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleNode(node.personnel_id)}
              className="text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          ) : (
            <span className="w-3" />
          )}
          <Icon className={`h-4 w-4 ${visual.color}`} />
          <span className="font-medium text-sm">{node.name}</span>
          <Badge variant="outline" className="text-xs">
            {node.role_slug}
          </Badge>
          {hasChildren && (
            <Badge variant="secondary" className="text-xs">
              {children.length} alt
            </Badge>
          )}
          {canDeactivate && depth > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                handleDeactivateClick(node);
              }}
              title="Pasifleştir"
            >
              <UserX className="h-3 w-3" />
            </Button>
          )}
        </div>
        {isExpanded &&
          hasChildren &&
          children
            .sort((a, b) => a.name.localeCompare(b.name, "tr"))
            .map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <>
      <Card className="border-indigo-200">
        <CardHeader className="bg-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Network className="h-6 w-6 text-indigo-700" />
              <div>
                <CardTitle className="text-indigo-700">
                  Organizasyon Ağacı
                </CardTitle>
                <CardDescription>
                  parent_personnel_id üzerinden hiyerarşi. Pasifleştirilen
                  personelin alt zinciri otomatik bir üst amire devredilir.
                </CardDescription>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                fetchPersonnel();
                if (selectedRootId) fetchChain(selectedRootId);
              }}
              disabled={loading || chainLoading}
              className="text-indigo-600 border-indigo-300"
            >
              <RefreshCw
                className={`h-3 w-3 mr-1 ${loading || chainLoading ? "animate-spin" : ""}`}
              />
              Yenile
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Yükleniyor...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* SOL: Kök personeller */}
              <div className="md:col-span-1">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">
                  Kök Personeller ({roots.length})
                </h3>
                <div className="border rounded divide-y max-h-96 overflow-y-auto">
                  {roots.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 italic">
                      Kök personel yok (orphan kayıtlar olabilir).
                    </p>
                  ) : (
                    roots.map((r) => {
                      const visual = ROLE_VISUAL[r.role] || ROLE_VISUAL.mudur;
                      const Icon = visual.icon;
                      return (
                        <button
                          key={r.id}
                          onClick={() => setSelectedRootId(r.id)}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 ${
                            selectedRootId === r.id ? "bg-indigo-50" : ""
                          }`}
                        >
                          <Icon className={`h-4 w-4 ${visual.color}`} />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{r.name}</div>
                            <div className="text-xs text-gray-500">
                              {r.role}
                            </div>
                          </div>
                          <ChevronRight
                            className={`h-3 w-3 transition-transform ${
                              selectedRootId === r.id ? "rotate-90" : ""
                            }`}
                          />
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* SAĞ: Hiyerarşi ağacı */}
              <div className="md:col-span-2">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">
                  {rootNode
                    ? `${rootNode.name} Alt Zinciri (${chainData.length} kişi)`
                    : "Hiyerarşi"}
                </h3>
                {!selectedRootId && (
                  <div className="border rounded p-6 text-center text-gray-500 text-sm">
                    Soldan bir kök personel seçin.
                  </div>
                )}
                {selectedRootId && chainLoading && (
                  <p className="text-center text-gray-500 py-4">Yükleniyor...</p>
                )}
                {selectedRootId && !chainLoading && rootNode && (
                  <div className="border rounded p-3 max-h-[600px] overflow-y-auto bg-white">
                    {renderNode(rootNode, 0)}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pasifleştirme modal */}
      {deactivateTarget && (
        <DeactivatePopupModal
          personnelId={deactivateTarget.personnel_id}
          personnelName={deactivateTarget.name}
          onClose={() => setDeactivateTarget(null)}
          onComplete={handleDeactivateComplete}
        />
      )}
    </>
  );
}
